import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

import { ELECTRIC_URLS } from 'hooks/use-electric/const'
import { ZAI_CHAT_URL_BY_ENVIRONMENT } from 'services/zai-chat/consts'
import getEnvFromLocation from 'utils/getEnvFromLocation'

import { redactSensitiveKeys } from './datadog.utils'
import type {
  IDatadogUser,
  TDatadogErrorLog,
  TDatadogGenericErrorLog,
  TDatadogOperationErrorLog,
  TDatadogRequestErrorLog,
} from './types'

const appEnv = getEnvFromLocation()
const shouldTrack = appEnv === 'production' || appEnv === 'staging' || appEnv === 'development'

/**
 * Origins of the first-party backends the app calls, read from their own env-url constants
 * (ELECTRIC_URLS, ZAI_CHAT_URL_BY_ENVIRONMENT) so this list can't drift from the actual hosts.
 */
const backendTracingOrigins = Array.from(
  new Set(
    [...Object.values(ELECTRIC_URLS), ...Object.values(ZAI_CHAT_URL_BY_ENVIRONMENT)].map(url => new URL(url).origin)
  )
)

function init() {
  if (shouldTrack) {
    datadogRum.init({
      applicationId: '7e3b8b12-2d77-446e-b4d6-8f52601a2802',
      clientToken: 'pub62efcf778bcfcb984f0e19209b2ef609',
      site: 'datadoghq.com',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      env: appEnv,
      service: 'frontend_insights',
      enableExperimentalFeatures: ['feature_flags'],
      allowedTracingUrls: [
        'https://insights.winnin.com',
        'https://staging.insights.winnin.com',
        'https://dev.insights.winnin.com',
        ...backendTracingOrigins,
      ],
      version: '1.0.0',
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
    })
    datadogRum.startSessionReplayRecording()

    datadogLogs.init({
      clientToken: 'pub62efcf778bcfcb984f0e19209b2ef609',
      site: 'datadoghq.com',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      env: appEnv,
      service: 'frontend_insights',
      version: '1.0.0',
      beforeSend: log => {
        const isAbortError = log?.error?.stack?.includes('AbortError: ')
        const isDashboardVersionConflict =
          log?.message?.includes('Invalid Version') && log?.message?.includes('JobDashboard')
        if (isAbortError || isDashboardVersionConflict) {
          return false
        }

        if (log?.view?.url?.includes('/dashboard') && log?.status === 'error') {
          log.message = `[Dashboard Error]: ${log?.message}`
        }

        if (log?.error?.stack?.includes(' intl ')) {
          log.message = `[Tolgee error]: ${log?.message}`
        }
      },
    })
    datadogLogs.setGlobalContextProperty('host', window.location.hostname)
  }
}

function setUser(user: IDatadogUser) {
  if (shouldTrack) {
    datadogRum.setUser({
      id: user.id,
      name: user?.firstName,
      role: user?.role,
      email: user?.email,
      company: user?.company?.name,
      jobTitle: user?.jobTitle ?? user?.jobTitleType?.name,
    })
  }
}

function addFeatureFlagEvaluation(key: string, value: unknown) {
  if (shouldTrack) {
    datadogRum.addFeatureFlagEvaluation(key, value)
  }
}

// Structured console.error picked up by datadogLogs (forwardErrorsToLogs).
function emitErrorLog(log: TDatadogErrorLog, errorPayload: Record<string, unknown>): void {
  console.error(log.name, {
    title: log.name,
    name: log.name,
    message: log.message,
    error: errorPayload,
  })
}

function getNetworkErrorDetails(networkError?: Error | null) {
  const statusCode = networkError?.['response']?.['status'] || networkError?.['statusCode']

  const details =
    networkError?.['result']?.['errors']?.map((error: { message: string }) => ({
      message: error.message,
    })) || []

  return { statusCode, details }
}

function dispatchRequestErrorLog(props: TDatadogRequestErrorLog): void {
  const { statusCode, details } = getNetworkErrorDetails(props.error?.networkError)

  emitErrorLog(props.log, {
    from: props.log.from,
    query: {
      ...props.query,
    },
    response: {
      name: props.error?.name,
      message: props.error?.message,
      statusCode,
      details: details.length ? details : props.error?.networkError?.['result'],
    },
  })
}

/**
 * Same payload shape as dispatchRequestErrorLog, but built from the pieces
 * available on the Apollo error link (operation + graphQLErrors/networkError)
 * so every operation error can be reported from a single place.
 */
function dispatchOperationErrorLog(props: TDatadogOperationErrorLog): void {
  const { graphQLErrors, networkError } = props
  const { statusCode, details } = getNetworkErrorDetails(networkError)

  emitErrorLog(props.log, {
    from: props.log.from,
    query: {
      name: props.operation.name,
      variables: redactSensitiveKeys(props.operation.variables),
    },
    response: {
      name: graphQLErrors?.length ? 'GraphQLErrors' : networkError?.name,
      message: graphQLErrors?.map(error => error.message).join('; ') || networkError?.message,
      statusCode,
      details: details.length ? details : graphQLErrors?.map(error => ({ message: error.message })),
    },
  })
}

function dispatchGenericErrorLog(props: TDatadogGenericErrorLog): void {
  emitErrorLog(props.log, {
    from: props.log.from,
    details: {
      name: props.error.name,
      message: props.error.message,
    },
  })
}

export default {
  init,
  setUser,
  addFeatureFlagEvaluation,
  dispatchRequestErrorLog,
  dispatchOperationErrorLog,
  dispatchGenericErrorLog,
}
